import * as React from 'react';
import { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
} from '@mui/material';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import type { AmenityReservation } from '@/types/v2/reservation';
import { getCommunityReservations } from '@/api/reservations';
import { useUser } from '@/hooks/v2/use-user';
import { paths } from '@/paths';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
  completed: 'info',
  no_show: 'error',
  waitlisted: 'info',
};

export function AllReservations(): React.JSX.Element {
  const { community } = useUser();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['community-reservations', community?.id],
    queryFn: () => getCommunityReservations(community?.id || ''),
    enabled: !!community?.id,
  });

  const reservations = data?.reservations || [];

  const columns = useMemo<MRT_ColumnDef<AmenityReservation>[]>(
    () => {
      const baseColumns: MRT_ColumnDef<AmenityReservation>[] = [
        {
          id: 'reservation',
          accessorFn: (row) => `${row.amenity?.name ?? ''} ${row.user?.first_name ?? ''} ${row.user?.last_name ?? ''}`,
          header: 'Reservation',
          size: 240,
          Cell: ({ row }) => {
            const reservation = row.original;
            const residentName = [reservation.user?.first_name, reservation.user?.last_name].filter(Boolean).join(' ');

            return (
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontWeight: 700 }}>{reservation.amenity?.name ?? 'Amenity'}</Box>
                <Box sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                  {residentName || 'Unknown resident'}
                  {reservation.user?.email ? ` · ${reservation.user.email}` : ''}
                </Box>
              </Box>
            );
          },
        },
        {
          accessorKey: 'start_time',
          header: 'Date & Time',
          size: 200,
          Cell: ({ row }) => (
            <Box>
              <Box sx={{ fontWeight: 600 }}>
                {dayjs.utc(row.original.start_time).local().format('MMM D, YYYY')}
              </Box>
              <Box sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                {dayjs.utc(row.original.start_time).local().format('h:mm A')} - {dayjs.utc(row.original.end_time).local().format('h:mm A')}
              </Box>
            </Box>
          ),
        },
        {
          accessorKey: 'status',
          header: 'Status',
          size: 120,
          Cell: ({ cell }) => (
            <Chip
              label={cell.getValue<string>().replace('_', ' ').toUpperCase()}
              size="small"
              color={statusColors[cell.getValue<string>()]}
              sx={{ fontWeight: 500 }}
            />
          ),
        },
        {
          id: 'details',
          accessorFn: (row) => `${row.purpose ?? ''} ${row.special_requests ?? ''} ${row.guest_count ?? ''}`,
          header: 'Details',
          size: 260,
          Cell: ({ row }) => {
            const reservation = row.original;
            return (
              <Box sx={{ maxWidth: 280 }}>
                <Box sx={{ color: reservation.purpose ? 'text.primary' : 'text.disabled', fontSize: '0.82rem' }}>
                  {reservation.purpose || 'No purpose provided'}
                </Box>
                <Box sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.25 }}>
                  {reservation.guest_count ? `${reservation.guest_count} guests` : 'Guests not specified'}
                  {reservation.special_requests ? ` · ${reservation.special_requests}` : ''}
                </Box>
              </Box>
            );
          },
        },
        {
          id: 'payment',
          header: 'Payment',
          size: 160,
          Cell: ({ row }) => {
            const res = row.original;
            const total = Number(res.total_fee_amount_charged ?? 0);
            const deposit = Number(res.deposit_amount_charged ?? 0);
            const fee = Number(res.fee_amount_charged ?? 0);
            const remainingFee = fee > 0
              ? (fee <= total && deposit > 0 ? Math.max(0, total - deposit) : fee)
              : 0;
            const hasDeposit = deposit > 0;
            const hasFee = remainingFee > 0;

            if (!hasDeposit && !hasFee && total === 0) {
              return <span style={{ color: 'var(--mui-palette-text-disabled)' }}>—</span>;
            }

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {total > 0 && (
                  <Box sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                    Total: ${total.toFixed(2)}
                  </Box>
                )}
                {hasDeposit && (
                  <Chip
                    label={`Deposit $${deposit.toFixed(2)} ${res.deposit_collected ? '✓' : '⏳'}`}
                    size="small"
                    color={res.deposit_collected ? 'success' : 'warning'}
                    variant={res.deposit_collected ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.68rem', height: 20 }}
                  />
                )}
                {hasFee && (
                  <Chip
                    label={`Fee $${remainingFee.toFixed(2)} ${res.fee_collected ? '✓' : '⏳'}`}
                    size="small"
                    color={res.fee_collected ? 'success' : 'warning'}
                    variant={res.fee_collected ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.68rem', height: 20 }}
                  />
                )}
                {hasDeposit && hasFee && res.deposit_collected && res.fee_collected && (
                  <Chip
                    label="Paid in Full"
                    size="small"
                    color="success"
                    sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600 }}
                  />
                )}
                {res.damage_charge_amount ? (
                  <Chip
                    label={`Damage $${Number(res.damage_charge_amount).toFixed(2)}`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ fontSize: '0.68rem', height: 20 }}
                  />
                ) : null}
                {res.deposit_refunded_at ? (
                  <Chip
                    label={`Refunded ${Number(res.deposit_refund_amount ?? res.deposit_amount_charged ?? 0) > 0 ? `$${Number(res.deposit_refund_amount ?? res.deposit_amount_charged).toFixed(2)}` : ''}`.trim()}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontSize: '0.68rem', height: 20 }}
                  />
                ) : null}
              </Box>
            );
          },
        },
        {
          accessorKey: 'created_at',
          header: 'Requested',
          size: 120,
          Cell: ({ cell }) => dayjs(cell.getValue<string>()).format('MMM D, YYYY'),
        },
      ];

      return baseColumns;
    },
    [],
  );

  const table = useMaterialReactTable<AmenityReservation>({
    columns,
    data: reservations,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      sorting: [
        {
          id: 'start_time',
          desc: true,
        },
      ],
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigate(paths.settings.community.reservations.reservation(row.original.uuid)),
      sx: { cursor: 'pointer' },
    }),
    state: {
      isLoading,
    },
  });

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: '4px 8px', alignItems: 'center' }}>
        <MRT_GlobalFilterTextField table={table} />
        <MRT_ToggleFiltersButton table={table} />
      </Box>

      <MaterialReactTable table={table} />
    </Paper>
  );
}





